import { AxiosError } from 'axios'
import { Box, Stack, Typography } from '@mui/material'
import { CORAL_200, CORAL_600, GRAY_700, GRAY_900, GREEN_200, GREEN_600 } from 'constants/styling/theme'
import { CancellationReason, DUPLICATE_DEAL_CANCELLATION_ERROR_CODE, DocumentProductCancellationReason, maxCancellationReasonTextCharacters } from 'constants/deal'
import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { DealDTOClient } from 'models/deal'
import { KeyboardEventKey } from 'constants/misc'
import { Label } from '../Label'
import { LinearProgressBar } from 'components/common/LinearProgressBar'
import { MUIBadge } from '../MUIBadge'
import { MUIButton } from '../MUIButton'
import { MUIInputField } from '../MUIInputField'
import { MUIRadio } from '../MUIRadio'
import Modal from '../Modals/Modal/Modal'
import { QueryStatus } from '../QueryStatus'
import TransitionAppear from '../TransitionAppear/TransitionAppear'
import { getTermsLink } from 'utils/localization'
import i18n from 'translations/i18n'
import moment from 'moment-timezone'
import { standardEnterEffect } from 'utils/animations'
import styles from './OrderCancellationPopup.module.sass'
import { supportEmail } from 'constants/contacts'
import { logger } from 'utils/logging'
import { useCancelOrder } from 'dataQueries/order.query'
import { useGalleryDeal } from 'components/pages/Gallery/_main/contexts/GalleryDeal.context'
import { useGalleryProduct } from 'components/pages/Gallery/_main/contexts/GalleryProduct.context'
import { resolveCancellationFee } from './cancellationFee'

/**
 * @interface Props Input properties
 */
export interface Props {
  /** Whether the popup is open */
  isOpen: boolean
  /** The data object */
  data: DealDTOClient
  /** Current Date time */
  dynamicNow: moment.Moment
  /** Onclick action triggered when user clicks the close button*/
  onClickClose: () => void
}

/**
 * @interface BaseProps Context-free input properties for the popup body.
 * The three values below are read from the Gallery contexts by the {@link OrderCancellationPopup}
 * wrapper, but taken as props here so the body can be reused outside the Gallery (e.g. the
 * Propstack embed status card, ISSUE-2018).
 */
export interface BaseProps extends Props {
  /** ISO shooting start of the earliest active assignment (server-computed), or null. */
  shootingStart: string | null
  /** Timezone of the earliest assignment; when unset the late-cancellation fee notice is not shown. */
  earliestAssignmentTimezone: string | undefined
  /** Whether the order contains a document product (hides the shooting-time fee meter). */
  isDocumentProduct: boolean
  /** Invoked once the cancellation mutation succeeds, so the host page can show its own confirmation. */
  onCancelSuccess?: () => void
  /** Invoked when the cancellation is rejected because the order is already cancelled server-side (409). */
  onAlreadyCancelled?: () => void
  /** Invoked with the picked reason (its enum value, never the free text) when the broker confirms, before the request. */
  onConfirm?: (reason: CancellationReason | DocumentProductCancellationReason) => void
  /** Invoked when the request is rejected for any reason but the already-cancelled 409, with the HTTP status (null without a response). */
  onCancelFailed?: (httpStatus: number | null) => void
}

/**
 * @component Context-free order cancellation popup body.
 * Takes the shooting/document values as props instead of reading the Gallery contexts, so it can
 * render outside a Gallery provider tree.
 */
export const OrderCancellationPopupBase: React.FC<BaseProps> = ({
  isOpen,
  data,
  dynamicNow,
  onClickClose,
  shootingStart,
  earliestAssignmentTimezone,
  isDocumentProduct,
  onCancelSuccess,
  onAlreadyCancelled,
  onConfirm,
  onCancelFailed,
}) => {
  const { t } = useTranslation(['order_cancallation_popup', 'cancellation_reasons', 'actions'])

  const cancelOrder = useCancelOrder()

  const [cancellationReason, setCancellationReason] = useState<CancellationReason | DocumentProductCancellationReason | undefined>(undefined)
  const [cancellationReasonText, setCancellationReasonText] = useState<string>('')

  const cancellationReasonOther = useMemo(() => CancellationReason.OTHER || DocumentProductCancellationReason.OTHER, [])
  const orderCreatedAt = useMemo(() => data?.createdAt ? moment(data.createdAt).toISOString() : null, [data])
  const lastCancelDateTimeWithoutFee = useMemo(() => moment(shootingStart).subtract(1, 'day'), [shootingStart])
  const hoursBeforeShooting = useMemo(() => {
    if (!shootingStart) return 0
    return moment.duration(moment(shootingStart).diff(dynamicNow, 'hours'))
  }, [dynamicNow, shootingStart])
  const isMaxCancellationReasonTextCharacters = useMemo(() => cancellationReasonText.length === maxCancellationReasonTextCharacters, [cancellationReasonText])
  const isCancelConfirmDisabled = useMemo(() => !cancellationReason || (cancellationReason === CancellationReason.OTHER && !cancellationReasonText) || cancelOrder.isPending || cancelOrder.isSuccess, [cancellationReason, cancellationReasonText, cancelOrder.isPending, cancelOrder.isSuccess])

  /** 
   * Hide the cancellation shooting information when:
   * order has document products
   */
  const hideCancellationShootingInfo = useMemo(() => isDocumentProduct, [isDocumentProduct])

  /** Whether the shooting starts within 24 hours, from the fee rule the status card's cancel events share, so the notice and the events agree. */
  const isLateCancellation = useMemo(() => !!data
    && resolveCancellationFee({ shootingStart, earliestAssignmentTimezone, isDocumentProduct, now: dynamicNow }).fee_tier === 'less_than_24h',
  [shootingStart, data, dynamicNow, earliestAssignmentTimezone, isDocumentProduct])

  const progressWithoutFee = useMemo(() => {

    if (isLateCancellation) return 100

    const totalValue = lastCancelDateTimeWithoutFee.diff(moment(orderCreatedAt))
    const currentValue = moment(dynamicNow).diff(moment(orderCreatedAt))

    return (currentValue / totalValue) * 100
  }, [dynamicNow, isLateCancellation, lastCancelDateTimeWithoutFee, orderCreatedAt])

  const progressWithFee = useMemo(() => {

    if (!isLateCancellation) return 0
    if (moment(shootingStart).isSameOrBefore(moment(dynamicNow))) return 100

    const totalValue = moment(shootingStart).diff(moment(lastCancelDateTimeWithoutFee))
    const currentValue = moment(dynamicNow).diff(moment(lastCancelDateTimeWithoutFee))

    return (currentValue / totalValue) * 100
  }, [dynamicNow, isLateCancellation, lastCancelDateTimeWithoutFee, shootingStart])

  // A close while the request runs would drop its outcome (the mutation's callbacks die with the unmount), so it waits
  const requestClose = useCallback(() => {
    if (cancelOrder.isPending) return
    onClickClose()
  }, [cancelOrder.isPending, onClickClose])

  const onExitPopUp = useCallback(() => {
    onClickClose()
    setCancellationReason(undefined)
    setCancellationReasonText('')
  }, [onClickClose])

  // A host that closes the dialog on success (the embed) must not get the delayed exit's close later
  const exitTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => () => clearTimeout(exitTimeoutRef.current), [])

  const handleConfirmCancel = useCallback(() => {
    if (!cancellationReason || cancelOrder.isPending || cancelOrder.isSuccess) return
    // The free text belongs to OTHER only; a text typed before switching to another reason must not travel
    const reason = cancellationReason === cancellationReasonOther ? (cancellationReasonText || cancellationReason) : cancellationReason
    onConfirm?.(cancellationReason)

    cancelOrder.mutate({ orderId: data.id, cancellationReason: reason }, {
      onSuccess: () => {
        logger.info('Order cancellation succeeded', {
          context: {
            component: 'OrderCancellationPopup',
            action: 'cancelOrder',
            metadata: { orderId: data.id, cancellationReason: reason }
          }
        })
        onCancelSuccess?.()
        exitTimeoutRef.current = setTimeout(() => onExitPopUp(), 3000)
      },
      onError: (error) => {
        logger.error('Order cancellation failed', error, {
          context: {
            component: 'OrderCancellationPopup',
            action: 'cancelOrder',
            metadata: { orderId: data.id, cancellationReason: reason }
          }
        })
        const httpStatus = (error as AxiosError | undefined)?.response?.status ?? null
        if (httpStatus === DUPLICATE_DEAL_CANCELLATION_ERROR_CODE) onAlreadyCancelled?.()
        else onCancelFailed?.(httpStatus)
      }
    })
  }, [cancelOrder, cancellationReason, cancellationReasonOther, cancellationReasonText, data.id, onAlreadyCancelled, onCancelFailed, onCancelSuccess, onConfirm, onExitPopUp])

  const handleInputKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === KeyboardEventKey.ENTER) e.preventDefault()
  }, [])

  const getCancellationReason = useCallback(() => {
    if (isDocumentProduct) return DocumentProductCancellationReason

    return CancellationReason
  }, [isDocumentProduct])

  return (
    <Modal
      className={styles.orderCancellationPopup}
      modalContentClassName={styles.orderCancellationPopupModalContent}
      isOpen={isOpen}
      onClickOutside={requestClose}
      onClose={requestClose}
      title={t('cancel_popup_title')}
      footerContent={
        <Stack direction="row" justifyContent="space-between" alignItems="center" paddingBottom=".8rem" gap={2}>

          <Typography variant="text-xs" color={GRAY_700}>
            <Trans t={t} i18nKey="cancel_terms_and_condition">
              <a className={styles.termsLink} href={getTermsLink(i18n.language)} rel="noopener noreferrer" target="_blank">&nbsp;</a>
            </Trans>
          </Typography>

          <Stack direction="row" gap=".8rem" flexShrink={0}>
            <MUIButton type="secondaryNoBorder" onClick={requestClose}>
              {t('actions:Back')}
            </MUIButton>

            <MUIButton type="orangePrimary" disabled={isCancelConfirmDisabled} onClick={handleConfirmCancel}>
              {t('cancel_order')}
            </MUIButton>
          </Stack>

        </Stack>
      }
    >
      <Stack gap="1.6rem" paddingBottom="2.4rem">

        <Stack gap=".2rem">
          <Typography variant="text-sm" color={GRAY_900}>
            <Trans
              t={t}
              i18nKey="fee_meter_title"
              values={{ order: data.id }}
            />
          </Typography>

          {/** SHOOTING INFORMATION */}
          {!hideCancellationShootingInfo &&
            <Stack gap=".2rem">
              <Stack direction="row" gap=".4rem" width="100%">
                <Box flexGrow={1} padding="1rem 0">
                  <LinearProgressBar value={progressWithoutFee} barBackgroundColor={GREEN_200} barColor={GREEN_600} />
                </Box>

                <MUIBadge label="24h" size="sm" color={isLateCancellation ? 'coral' : 'gray'} />

                <Box flexGrow={1} maxWidth="10rem" padding="1rem 0">
                  <LinearProgressBar value={progressWithFee} barBackgroundColor={CORAL_200} barColor={CORAL_600} />
                </Box>
              </Stack>

              <Typography variant="text-xs" color={isLateCancellation ? CORAL_600 : GRAY_700}>
                <Trans
                  t={t}
                  i18nKey={isLateCancellation ? 'cancel_order_with_fee' : 'cancel_order_without_fee'}
                  values={{ hours: hoursBeforeShooting }}
                >
                  <a href={`mailto:${supportEmail}`} style={{ textDecoration: 'none' }}>&nbsp;</a>
                </Trans>
              </Typography>

              <Typography variant="text-sm" color={GRAY_700}>
                {t('cancel_order_reschedule')}
              </Typography>
            </Stack>
          }

        </Stack>

        {/** REASONS */}
        <Stack gap=".8rem">
          <Label text={t('cancel_order_reason_message')} />

          {Object.values(getCancellationReason()).map(reason => {
            return (
              <Stack key={reason} gap=".8rem" flexDirection="row">
                <MUIRadio
                  checked={cancellationReason === reason}
                  onChange={() => setCancellationReason(reason)}
                />

                <Label color={GRAY_900} text={t(`cancellation_reasons:${reason}`)} />
              </Stack>
            )
          })}

          <TransitionAppear visible={cancellationReason === cancellationReasonOther} onEnter={standardEnterEffect}>
            <MUIInputField
              label={t('cancellation_reasons:text')}
              value={cancellationReasonText}
              isError={!!isMaxCancellationReasonTextCharacters}
              errorText={t('cancellation_reason_characters_length_message', { count: maxCancellationReasonTextCharacters })}
              onChange={e => setCancellationReasonText(e.target.value.replace(/(\r\n|\n|\r)/gm, '').slice(0, maxCancellationReasonTextCharacters))}
              onKeyDown={e => handleInputKeyDown(e)}
            />
          </TransitionAppear>
        </Stack>

        <QueryStatus
          query={cancelOrder}
          onPurge={() => cancelOrder.reset()}
          successMessage={t('cancel_success')}
          spaceTopRem={2}
          // A rejected mutation keeps its status on `error` (`data` stays undefined), so the 409
          // "already cancelled" case must be read from the AxiosError, not the legacy data shape.
          errorMessage={(cancelOrder.error as AxiosError | null)?.response?.status === DUPLICATE_DEAL_CANCELLATION_ERROR_CODE
            ? t('already_cancelled')
            : undefined
          }
        />

      </Stack>
    </Modal>
  )
}

/**
 * @component Display order cancellation details in a popup.
 * Gallery-bound wrapper that reads the shooting/document values from the Gallery contexts and
 * delegates rendering to {@link OrderCancellationPopupBase}. Its public `Props` are unchanged so
 * existing call sites keep working identically.
 * @example
 * <OrderCancellationPopup isOpen={true} data={data} onClickClose={() => {}} />
 */
export const OrderCancellationPopup: React.FC<Props> = (props) => {
  const { shootingStart, earliestAssignment } = useGalleryDeal()
  const { isDocumentProduct } = useGalleryProduct()

  return (
    <OrderCancellationPopupBase
      {...props}
      shootingStart={shootingStart}
      earliestAssignmentTimezone={earliestAssignment?.timezone ?? undefined}
      isDocumentProduct={isDocumentProduct}
    />
  )
}
